import { useCallback, useEffect, useState } from 'react'
import { Loader2, RefreshCw, Activity, AlertTriangle, ChevronRight, ChevronDown } from 'lucide-react'
import toast from 'react-hot-toast'
import {
  monitoringApi,
  type ProcessingRun,
  type HealthSummary,
  type ModelEvalRow,
  type EditorFeedbackResponse,
  type RunDetail,
} from '../services/monitoringApi'

type Tab = 'health' | 'eval' | 'feedback'

const RUN_STATUS_CLASS: Record<string, string> = {
  ready: 'bg-emerald-500/20 text-emerald-300',
  partial: 'bg-amber-500/20 text-amber-300',
  failed: 'bg-red-500/20 text-red-300',
  processing: 'bg-sky-500/20 text-sky-300',
  pending: 'bg-white/10 text-[var(--rq-text-soft)]',
}

function StatusBadge({ status }: { status: string }) {
  const cls = RUN_STATUS_CLASS[status] || 'bg-white/10 text-[var(--rq-text-soft)]'
  return <span className={`px-2 py-0.5 text-xs rounded ${cls}`}>{status}</span>
}

function fmtTime(iso: string | null): string {
  if (!iso) return '—'
  try { return new Date(iso).toLocaleString() } catch { return iso }
}

function fmtCost(usd: number | null | undefined): string {
  if (usd == null) return '—'
  if (usd === 0) return '$0'
  // Small per-call/per-group costs: show enough precision to be meaningful.
  return usd < 1 ? `$${usd.toFixed(4)}` : `$${usd.toFixed(2)}`
}

/** A rate in 0..1 as a percentage. null is "no data", which is not zero: an
 *  empty dashboard must not read as a perfect score. */
function fmtRate(rate: number | null | undefined): string {
  if (rate == null) return '—'
  return `${(rate * 100).toFixed(1)}%`
}

function MetricCard({ label, value, detail }: {
  label: string
  value: string
  detail: string
}) {
  return (
    <div className="rq-card p-4">
      <p className="text-[10px] uppercase tracking-wider text-[var(--rq-text-soft)]">{label}</p>
      <p className="text-2xl font-semibold text-[var(--rq-text)] tabular-nums mt-1">{value}</p>
      <p className="text-[11px] text-[var(--rq-text-soft)] mt-1">{detail}</p>
    </div>
  )
}

export default function MonitoringPage() {
  const [tab, setTab] = useState<Tab>('health')
  const [days, setDays] = useState(7)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // App health
  const [summary, setSummary] = useState<HealthSummary | null>(null)
  const [runs, setRuns] = useState<ProcessingRun[]>([])
  const [expandedRunId, setExpandedRunId] = useState<string | null>(null)
  const [runDetail, setRunDetail] = useState<RunDetail | null>(null)

  // Model eval
  const [evalRows, setEvalRows] = useState<ModelEvalRow[]>([])

  // Editor feedback (product quality)
  const [feedback, setFeedback] = useState<EditorFeedbackResponse | null>(null)

  const fetchAll = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [s, r, e, f] = await Promise.allSettled([
        monitoringApi.summary(days),
        monitoringApi.listRuns({ days, limit: 100 }),
        monitoringApi.modelEval({ days }),
        monitoringApi.editorFeedback({ days }),
      ])
      if (s.status === 'fulfilled') setSummary(s.value)
      if (r.status === 'fulfilled') setRuns(r.value.runs)
      if (e.status === 'fulfilled') setEvalRows(e.value.rows)
      if (f.status === 'fulfilled') setFeedback(f.value)
      if ([s, r, e, f].some(p => p.status === 'rejected')) {
        setError('Some monitoring data failed to load')
      }
    } catch {
      setError('Failed to load monitoring data')
      toast.error('Failed to load monitoring data')
    } finally {
      setLoading(false)
    }
  }, [days])

  useEffect(() => { fetchAll() }, [fetchAll])

  const toggleRun = useCallback(async (runId: string) => {
    if (expandedRunId === runId) {
      setExpandedRunId(null)
      setRunDetail(null)
      return
    }
    setExpandedRunId(runId)
    setRunDetail(null)
    try {
      setRunDetail(await monitoringApi.getRun(runId))
    } catch {
      toast.error('Failed to load run detail')
    }
  }, [expandedRunId])

  return (
    <div className="rq-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="rq-title">Monitoring</h1>
          <p className="rq-subtitle">Ingest run health &amp; model performance</p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={days}
            onChange={e => setDays(Number(e.target.value))}
            className="rq-input-dark rounded-lg px-3 py-1.5 text-xs"
          >
            <option value={1}>Last 24h</option>
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
          <button onClick={fetchAll} disabled={loading} className="rq-btn-ghost px-3 py-1.5 text-xs disabled:opacity-50">
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 mb-5 border-b border-white/[0.06]">
        {(['health', 'eval', 'feedback'] as Tab[]).map(t => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-4 py-2 text-sm -mb-px border-b-2 ${
              tab === t ? 'border-[var(--rq-blue)] text-[var(--rq-text)]' : 'border-transparent text-[var(--rq-text-soft)]'
            }`}
          >
            {t === 'health' ? 'App Health' : t === 'eval' ? 'Model Eval' : 'Editor Feedback'}
          </button>
        ))}
      </div>

      {error && (
        <div className="rq-card p-4 border border-red-500/40 bg-red-500/10 mb-6 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-red-300" />
          <p className="text-sm text-red-200">{error}</p>
        </div>
      )}

      {tab === 'health' && (
        <>
          {/* Status summary */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
            <div className="rq-card p-4">
              <p className="text-2xl font-bold">{summary?.total_runs ?? '—'}</p>
              <p className="text-xs text-[var(--rq-text-soft)]">Total runs</p>
            </div>
            {['ready', 'partial', 'failed', 'processing', 'pending'].map(st => (
              <div key={st} className="rq-card p-4">
                <p className="text-2xl font-bold">{summary?.by_status?.[st] ?? 0}</p>
                <p className="text-xs text-[var(--rq-text-soft)] capitalize">{st}</p>
              </div>
            ))}
          </div>

          {summary && Object.keys(summary.failed_steps).length > 0 && (
            <div className="rq-card p-4 mb-6">
              <p className="text-sm font-semibold mb-2">Failed steps</p>
              <div className="flex flex-wrap gap-2">
                {Object.entries(summary.failed_steps).map(([name, n]) => (
                  <span key={name} className="px-2 py-1 text-xs rounded bg-red-500/15 text-red-200">
                    {name}: {n}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Runs table */}
          <div className="rq-card">
            <div className="px-5 py-3 border-b border-white/[0.06] text-sm font-semibold">Ingest runs</div>
            {loading && !runs.length ? (
              <div className="py-12 flex justify-center"><Loader2 className="w-5 h-5 animate-spin" /></div>
            ) : runs.length === 0 ? (
              <div className="py-12 text-center text-xs text-[var(--rq-text-soft)]">No runs in this window</div>
            ) : (
              <div className="divide-y divide-white/[0.04]">
                {runs.map(run => (
                  <div key={run.id}>
                    <button
                      onClick={() => toggleRun(run.id)}
                      className="w-full flex items-center gap-3 px-5 py-2.5 hover:bg-white/[0.02] text-left"
                    >
                      {expandedRunId === run.id
                        ? <ChevronDown className="w-3.5 h-3.5 text-[var(--rq-text-soft)]" />
                        : <ChevronRight className="w-3.5 h-3.5 text-[var(--rq-text-soft)]" />}
                      <span className="text-sm min-w-0 flex-1 truncate" title={run.asset_filename || `Asset ${run.asset_id}`}>
                        {run.asset_filename || `Asset ${run.asset_id}`}
                        <span className="text-xs text-[var(--rq-text-soft)] ml-2">#{run.asset_id}</span>
                      </span>
                      <StatusBadge status={run.status} />
                      <span className="text-xs text-[var(--rq-text-soft)]">{fmtTime(run.started_at)}</span>
                      <span className="text-xs text-[var(--rq-text-soft)]">
                        {run.steps.completed}/{run.steps.total} steps
                        {run.steps.failed > 0 && <span className="text-red-300"> · {run.steps.failed} failed</span>}
                      </span>
                      <span className="text-xs text-[var(--rq-text-soft)] w-16 text-right">
                        {run.duration_s != null ? `${run.duration_s}s` : '—'}
                      </span>
                    </button>

                    {expandedRunId === run.id && (
                      <div className="px-10 py-3 bg-white/[0.015] text-xs">
                        {!runDetail ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <div className="space-y-3">
                            <div>
                              <p className="text-[var(--rq-text-soft)] mb-1">Steps</p>
                              {runDetail.steps.map(s => (
                                <div key={s.id} className="flex items-center gap-3 py-0.5">
                                  <span className="w-44">{s.step_name}</span>
                                  <StatusBadge status={s.status} />
                                  <span className="w-16 text-right text-[var(--rq-text-soft)]">
                                    {s.duration_s != null ? `${s.duration_s}s` : '—'}
                                  </span>
                                  <span className="text-[var(--rq-text-soft)] flex-1 truncate">
                                    {s.model_name || s.source_service || ''}
                                  </span>
                                  {/* Repairs are not failures: the step
                                      completed, having patched up the model's
                                      output to get there. Amber, not red. */}
                                  {s.repairs && Object.entries(s.repairs).map(([name, n]) => (
                                    <span
                                      key={name}
                                      className="px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 whitespace-nowrap"
                                      title="Repaired while parsing the model's output"
                                    >
                                      {name.replace(/_/g, ' ')}: {n}
                                    </span>
                                  ))}
                                  {s.error_message && <span className="text-red-300 truncate">{s.error_message}</span>}
                                </div>
                              ))}
                            </div>
                            <div>
                              <p className="text-[var(--rq-text-soft)] mb-1">Model calls</p>
                              {runDetail.model_runs.length === 0
                                ? <span className="text-[var(--rq-text-soft)]">none</span>
                                : runDetail.model_runs.map(m => (
                                  <div key={m.id} className="flex items-center gap-3 py-0.5">
                                    <span className="w-44">{m.task_type}</span>
                                    <span className="w-40 text-[var(--rq-text-soft)]">{m.provider}/{m.model_name}</span>
                                    <StatusBadge status={m.status} />
                                    <span className="text-[var(--rq-text-soft)]">{m.latency_ms != null ? `${m.latency_ms}ms` : '—'}</span>
                                    <span className="text-[var(--rq-text-soft)]">{fmtCost(m.cost_estimate)}</span>
                                  </div>
                                ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {tab === 'eval' && (
        <div className="rq-card overflow-hidden">
          <div className="px-5 py-3 border-b border-white/[0.06] text-sm font-semibold flex items-center gap-2">
            <Activity className="w-4 h-4" /> Model evaluation
            <span className="text-xs font-normal text-[var(--rq-text-soft)]">— per task × provider × model, last {days}d</span>
            <span className="ml-auto text-xs font-normal text-[var(--rq-text-soft)]">
              Est. spend: <span className="text-[var(--rq-text)] font-semibold">
                {fmtCost(evalRows.reduce((s, r) => s + (r.total_cost_estimate || 0), 0))}
              </span>
            </span>
          </div>
          {loading && !evalRows.length ? (
            <div className="py-12 flex justify-center"><Loader2 className="w-5 h-5 animate-spin" /></div>
          ) : evalRows.length === 0 ? (
            <div className="py-12 text-center text-xs text-[var(--rq-text-soft)]">No model calls in this window</div>
          ) : (
            <table className="w-full text-xs">
              <thead className="text-[var(--rq-text-soft)] border-b border-white/[0.06]">
                <tr className="text-left">
                  <th className="px-4 py-2 font-medium">Task</th>
                  <th className="px-4 py-2 font-medium">Provider / Model</th>
                  <th className="px-4 py-2 font-medium text-right">Calls</th>
                  <th className="px-4 py-2 font-medium text-right">Success</th>
                  <th className="px-4 py-2 font-medium text-right">Avg latency</th>
                  <th className="px-4 py-2 font-medium text-right">Avg tok in/out</th>
                  <th className="px-4 py-2 font-medium text-right">Cost</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/[0.04]">
                {evalRows.map((r, i) => {
                  const successPct = r.count ? Math.round((r.success_count / r.count) * 100) : 0
                  return (
                    <tr key={i} className="hover:bg-white/[0.02]">
                      <td className="px-4 py-2">{r.task_type || '—'}</td>
                      <td className="px-4 py-2 text-[var(--rq-text-soft)]">{r.provider || '—'}/{r.model_name || '—'}</td>
                      <td className="px-4 py-2 text-right">{r.count}</td>
                      <td className={`px-4 py-2 text-right ${successPct === 100 ? 'text-emerald-300' : r.failure_count > 0 ? 'text-amber-300' : ''}`}>
                        {successPct}%
                      </td>
                      <td className="px-4 py-2 text-right">{r.avg_latency_ms != null ? `${r.avg_latency_ms}ms` : '—'}</td>
                      <td className="px-4 py-2 text-right">
                        {r.avg_input_tokens != null ? Math.round(r.avg_input_tokens) : '—'}/{r.avg_output_tokens != null ? Math.round(r.avg_output_tokens) : '—'}
                      </td>
                      <td className="px-4 py-2 text-right">{fmtCost(r.total_cost_estimate)}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {tab === 'feedback' && (
        <div className="space-y-5">
          <p className="text-xs text-[var(--rq-text-soft)]">
            What editors did to the drafts the model proposed. Model Eval says whether
            calls succeeded; this says whether the output was any good.
          </p>

          {!feedback ? (
            <div className="rq-card p-6 text-sm text-[var(--rq-text-soft)]">
              No editor feedback available.
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <MetricCard
                  label="Acceptance"
                  value={fmtRate(feedback.acceptance.acceptance_rate)}
                  detail={`${feedback.acceptance.proposed - feedback.acceptance.excluded} of ${feedback.acceptance.proposed} proposed clips kept`}
                />
                <MetricCard
                  label="Locked"
                  value={fmtRate(feedback.acceptance.lock_rate)}
                  detail={`${feedback.acceptance.locked} clips the editor pinned`}
                />
                <MetricCard
                  label="Regenerated"
                  value={feedback.regeneration.regenerations_per_assembly?.toFixed(2) ?? '—'}
                  detail={`${feedback.regeneration.sections_regenerated} sections across ${feedback.regeneration.assemblies_created} drafts`}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <MetricCard
                  label="Order kept"
                  value={feedback.order_churn.rank_correlation?.toFixed(2) ?? '—'}
                  detail={
                    feedback.order_churn.clips_compared === 0
                      ? 'No drafts generated since order recording began'
                      : `1.00 means untouched, -1.00 reversed, over ${feedback.order_churn.clips_compared} clips`
                  }
                />
                <MetricCard
                  label="Clips moved"
                  value={fmtRate(feedback.order_churn.moved_rate)}
                  detail={`${feedback.order_churn.moved_count ?? 0} clips dragged out of their proposed position`}
                />
              </div>

              <div className="rq-card p-4">
                <h3 className="text-sm font-medium text-[var(--rq-text)]">Score calibration</h3>
                <p className="text-xs text-[var(--rq-text-soft)] mt-1 mb-3">
                  Exclusion rate per score bucket. The bars should shorten as the score
                  rises: that is the scorer predicting what editors keep. A flat line
                  means the score carries no information, whatever its average.
                </p>
                {feedback.score_calibration.length === 0 ? (
                  <p className="text-xs text-[var(--rq-text-soft)]">Not enough scored clips yet.</p>
                ) : (
                  <div className="space-y-1.5">
                    {feedback.score_calibration.map(row => (
                      <div key={row.bucket} className="flex items-center gap-3">
                        <span className="w-20 text-[10px] tabular-nums text-[var(--rq-text-soft)] flex-shrink-0">
                          {row.score_from.toFixed(2)}–{row.score_to.toFixed(2)}
                        </span>
                        <div className="flex-1 h-4 rounded bg-white/[0.04] overflow-hidden">
                          <div
                            className="h-full bg-amber-500/50"
                            style={{ width: `${(row.exclusion_rate ?? 0) * 100}%` }}
                          />
                        </div>
                        <span className="w-24 text-[10px] tabular-nums text-[var(--rq-text-soft)] text-right flex-shrink-0">
                          {fmtRate(row.exclusion_rate)} of {row.proposed}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  )
}
